import React, { useEffect } from 'react';

interface FeatureDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content: string;
}

interface ListItem {
  title: string;
  description: string;
}

const FeatureDetailModal: React.FC<FeatureDetailModalProps> = ({
  isOpen,
  onClose,
  title,
  content
}) => {
  // Effet pour empêcher le scroll et masquer la navbar quand la modale est ouverte
  useEffect(() => {
    // Cibler les éléments potentiels de la navbar
    // En supposant que la navbar a une position fixed et est en haut de la page
    const navbarElements = document.querySelectorAll('header, nav, .header, .navbar, .nav');
    
    if (isOpen) {
      // Désactiver le scroll sur le body quand la modale est ouverte
      document.body.style.overflow = 'hidden';
      
      // Masquer la navbar
      navbarElements.forEach(element => {
        if (element instanceof HTMLElement) {
          // Sauvegarder le z-index actuel pour pouvoir le restaurer
          element.dataset.originalZIndex = element.style.zIndex;
          element.dataset.originalVisibility = element.style.visibility;
          element.style.visibility = 'hidden';
          element.style.zIndex = '-1';
        }
      });
      
      // Ajouter des styles pour la scrollbar directement dans le head
      const styleElement = document.createElement('style');
      styleElement.id = 'modal-scrollbar-styles';
      styleElement.textContent = `
        ::-webkit-scrollbar {
          width: 8px;
          background-color: #1E293B;
        }
        ::-webkit-scrollbar-thumb {
          background-color: #2a3a4f;
          border-radius: 4px;
        }
        ::-webkit-scrollbar-track {
          background-color: #1E293B;
        }
      `;
      document.head.appendChild(styleElement);
    } else {
      // Réactiver le scroll quand la modale est fermée
      document.body.style.overflow = 'auto';
      
      // Restaurer la navbar
      navbarElements.forEach(element => {
        if (element instanceof HTMLElement) {
          // Restaurer les propriétés d'origine
          element.style.zIndex = element.dataset.originalZIndex || '';
          element.style.visibility = element.dataset.originalVisibility || '';
        }
      });
      
      // Supprimer les styles de scrollbar
      const styleElement = document.getElementById('modal-scrollbar-styles');
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    }

    // Nettoyage quand le composant est démonté
    return () => {
      document.body.style.overflow = 'auto';
      
      // S'assurer que la navbar est restaurée
      navbarElements.forEach(element => {
        if (element instanceof HTMLElement) {
          element.style.zIndex = element.dataset.originalZIndex || '';
          element.style.visibility = element.dataset.originalVisibility || '';
        }
      });
      
      // S'assurer que les styles de scrollbar sont supprimés
      const styleElement = document.getElementById('modal-scrollbar-styles');
      if (styleElement) {
        document.head.removeChild(styleElement);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  // Séparer le contenu en deux parties: introduction et liste d'éléments
  const contentParts = content.split('\n\n');
  const introduction = contentParts[0];
  
  // Parser les éléments de liste structurés
  const listItems: ListItem[] = [];
  if (contentParts.length > 1) {
    const items = contentParts[1].split('\n');
    items.forEach(item => {
      if (item.trim()) {
        // Format: "titre:::description"
        const parts = item.split(':::');
        const itemTitle = parts[0].replace(/^[•-]\s*/, '').trim();
        const itemDescription = parts.length > 1 ? parts[1].trim() : '';
        
        listItems.push({
          title: itemTitle,
          description: itemDescription
        });
      }
    });
  }

  return (
    <div className="tw-fixed tw-inset-0 tw-bg-black/50 tw-flex tw-items-center tw-justify-center tw-z-50 tw-p-4">
      <div 
        className="tw-bg-[#1E293B] tw-shadow-lg tw-max-w-2xl tw-w-full tw-max-h-[90vh] tw-flex tw-flex-col tw-overflow-hidden" 
        style={{ borderRadius: '0.75rem' }}
      >
        {/* En-tête fixe avec bordure de séparation */}
        <div 
          className="tw-sticky tw-top-0 tw-z-10 tw-bg-[#1E293B] tw-p-6 tw-pb-4 tw-border-b tw-border-gray-600" 
          style={{ 
            borderRadius: '0.75rem 0.75rem 0 0',
            borderBottom: '0.5px solid #4B5563'
          }}
        >
          <div className="tw-flex tw-justify-between tw-items-start">
            <h3 
              className="tw-text-xl tw-font-semibold tw-text-white" 
              style={{ paddingLeft: '30px', paddingRight: '30px' }}
            >
              {title}
            </h3>
            <button 
              onClick={onClose}
              className="tw-text-gray-400 hover:tw-text-white tw-text-2xl"
            >
              &times;
            </button>
          </div>
        </div>
        
        {/* Contenu défilant avec scrollbar personnalisée */}
        <div 
          className="tw-overflow-auto tw-p-6 tw-pt-4"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#2a3a4f #1E293B',
         }}
        >
          <div className="tw-text-gray-300 tw-pr-[3em]">
            <p className="tw-pl-[30px]">{introduction}</p>
            
            {listItems.length > 0 && (
              <ul className="tw-mt-4 tw-space-y-6">
                {listItems.map((item, index) => (
                  <li key={index} className="tw-flex tw-flex-col">
                    <div className="tw-flex tw-items-start">
                      <span className="tw-text-[#26f0ff] tw-mr-2 tw-mt-0.5 tw-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-5 tw-w-5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </span>
                      <span className="tw-font-bold tw-text-white tw-text-left">{item.title}</span>
                    </div>
                    {item.description && (
                      <div className="tw-ml-7 tw-text-sm tw-text-gray-400 tw-text-left">
                        {item.description}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          <div className="tw-mt-6 tw-flex tw-justify-end">
            <button
              onClick={onClose}
              className="tw-bg-[#26f0ff] tw-text-[#1E293B] tw-font-medium tw-py-2 tw-px-4 tw-rounded hover:tw-bg-[#1ad9e4] tw-transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FeatureDetailModal; 